import type { SxProps, Theme } from '@mui/material/styles';
import type { StackProps } from '@mui/material';

export type BoostStatus = 'active' | 'waiting' | 'empty';
export type BoostDuration = 2 | 4 | 8 | 12 | 24;

const MOBILE_BP = 769;

export const getCardSx = (queued?: boolean): SxProps<Theme> => ({
  backgroundColor: '#1E1F25',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  flexShrink: 0,
  position: 'relative',
  filter: queued ? 'grayscale(20%)' : 'none',
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 0,
  },
});

export const topRowSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  '@media (max-width: 426px)': {
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
    gap: 2,
  },
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    width: 'auto',
    justifyContent: 'flex-start',
  },
};

export const leftStackProps: StackProps = {
  direction: 'row',
  alignItems: 'center',
  gap: { xs: 1, sm: 2 } as StackProps['gap'],
};

export const getBadgeImgSx = (queued?: boolean): SxProps<Theme> => ({
  width: queued ? 40 : 50,
  height: queued ? 40 : 50,
  objectFit: 'contain',
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    width: queued ? 53 : 67,
    height: queued ? 53 : 67,
  },
});

export const getTitleTextSx = (queued?: boolean): SxProps<Theme> => ({
  fontSize: queued ? 13 : 15,
  fontWeight: 'bold',
  opacity: queued ? 0.8 : 1,
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    fontSize: queued ? 16 : 20,
  },
});

export const chipDesktopSx: SxProps<Theme> = {
  display: 'none',
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    display: 'flex',
  },
};

export const chipMobileSx: SxProps<Theme> = {
  display: 'flex',
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    display: 'none',
  },
};

export const rightStackProps: StackProps = {
  direction: 'row',
  alignItems: 'center',
  gap: { xs: 1, sm: 2 } as StackProps['gap'],
  sx: {
    width: '100%',
    justifyContent: 'space-between',
    [`@media (min-width: ${MOBILE_BP}px)`]: {
      width: 'auto',
      justifyContent: 'flex-start',
    },
  },
};

export const timerStackProps: StackProps = {
  direction: 'row',
  alignItems: 'center',
  gap: 0.5,
};

export const getTimerLabelTextSx = (queued?: boolean): SxProps<Theme> => ({
  fontSize: queued ? 14 : 16,
  color: '#CCD4FF',
  opacity: queued ? 0.8 : 1,
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    fontSize: queued ? 16 : 20,
  },
});

export const getTimerValueTextSx = (queued?: boolean): SxProps<Theme> => ({
  fontSize: queued ? 14 : 16,
  fontWeight: 'bold',
  opacity: queued ? 0.8 : 1,
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    fontSize: queued ? 16 : 20,
  },
});

export const queuedStatusChipSx: SxProps<Theme> = {
  '& .MuiChip-label': { opacity: 0.8 },
};

export const deleteButtonSx: SxProps<Theme> = {
  color: '#CCD4FF',
  '@media (max-width: 426px)': {
    position: 'absolute',
    top: 8,
    right: 8,
  },
};

export const getEmptyIconBoxSx = (queued?: boolean): SxProps<Theme> => ({
  p: queued ? 0.75 : 1,
  borderRadius: '12px',
  backgroundColor: '#99A8FF1A',
  border: '1px solid #FFFFFF1A',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const EMPTY_ICON_SIZE = { normal: 42, queued: 32 };

export const getEmptyTitleTextSx = (queued?: boolean): SxProps<Theme> => ({
  fontSize: queued ? 13 : 15,
  color: '#CCD4FF',
  fontWeight: 'bold',
  opacity: queued ? 0.8 : 1,
  [`@media (min-width: ${MOBILE_BP}px)`]: {
    fontSize: queued ? 16 : 20,
  },
});
